import { useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { earlyAccessSchema, getFieldErrors, type FieldErrors } from "@/lib/validations";
import { FormError } from "@/components/ui/form-error";

type Role = z.infer<typeof earlyAccessSchema>["userType"];

const ROLES: { value: Role; label: string }[] = [
  { value: "brand", label: "Brand" },
  { value: "remaker", label: "Remaker" },
  { value: "sorting-facility", label: "Sorting facility" },
];

export function EarlyAccessDialog({ children, defaultOpen = false }: { children?: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState<Role | "">("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const { toast } = useToast();

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setTimeout(() => {
        setEmail(""); setUserType(""); setGdprConsent(false); setSubmitted(false); setErrors({});
      }, 300);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = earlyAccessSchema.safeParse({ email, userType, gdprConsent });
    if (!result.success) { setErrors(getFieldErrors(result)); return; }
    setErrors({});
    setIsLoading(true);
    try {
      const { error } = await supabase.from("early_access_signups").insert({
        email: result.data.email,
        user_type: result.data.userType,
        gdpr_consent_at: new Date().toISOString(),
      });
      if (error && error.code !== "23505") throw error;
      if (!error) {
        supabase.functions.invoke("send-early-access-confirmation", {
          body: { email: result.data.email, userType: result.data.userType },
        }).catch((e) => console.error("Confirmation email failed:", e));
      }
      setSubmitted(true);
    } catch (err) {
      console.error("Early access error:", err);
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    } finally { setIsLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-md rounded-none">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl uppercase font-black">Request early access</DialogTitle>
              <DialogDescription>
                We let new brands in every day. Drop your details and we will send your invite as soon as your spot opens.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 mt-2">
              <div className="space-y-2">
                <Label htmlFor="ea-email">Work email</Label>
                <Input
                  id="ea-email" type="email" placeholder="you@company.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={errors.email ? "border-destructive" : ""}
                />
                <FormError message={errors.email} />
              </div>
              <div className="space-y-2">
                <Label>I am a</Label>
                <div className="grid grid-cols-3 gap-2">
                  {ROLES.map((r) => (
                    <Button
                      key={r.value} type="button" size="sm"
                      variant={userType === r.value ? "default" : "outline"}
                      className="uppercase rounded-none text-xs"
                      onClick={() => setUserType(r.value)}
                    >
                      {r.label}
                    </Button>
                  ))}
                </div>
                <FormError message={errors.userType} />
              </div>
              <div className="space-y-2">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="ea-gdpr" checked={gdprConsent}
                    onCheckedChange={(c) => setGdprConsent(c === true)}
                    className={`mt-1 ${errors.gdprConsent ? "border-destructive" : ""}`}
                  />
                  <Label htmlFor="ea-gdpr" className="text-sm font-normal text-muted-foreground cursor-pointer leading-relaxed">
                    I agree that HIDNstock stores my email to contact me about early access, in line with the privacy policy.
                  </Label>
                </div>
                <FormError message={errors.gdprConsent} />
              </div>
              <Button type="submit" className="w-full uppercase rounded-none" size="lg" disabled={isLoading}>
                {isLoading ? "Sending..." : "Request access"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-6">
            <CheckCircle className="h-10 w-10 mx-auto mb-4" />
            <DialogHeader>
              <DialogTitle className="text-2xl uppercase font-black text-center">You're on the list</DialogTitle>
              <DialogDescription className="text-base mt-2 text-center">
                We open new spots every day. Watch your inbox for your invite.
              </DialogDescription>
            </DialogHeader>
            <Button onClick={() => handleOpenChange(false)} variant="outline" className="mt-6 uppercase rounded-none">
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
